import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, AlertTriangle, Send } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Skeleton } from '../../components/ui/Skeleton';
import { useMaterialBalance } from '../../hooks/useMaterialBalance';
import { useEquipos } from '../../hooks/useEquipos';
import { useCreatePedido } from '../../hooks/usePedidos';
import { toast } from '../../components/ui/Toast';
import { useAuthStore } from '../../stores/auth-store';

type PedidoTipo = 'materiales' | 'equipos' | 'ambos';

interface ItemQuantity {
  materialId: string;
  cantidad: number;
  justificacion: string;
}

interface ConsumibleItem {
  nombre: string;
  unidad: string;
  cantidad: number;
  justificacion: string;
}

interface SimpleItem {
  descripcion: string;
  cantidad: number;
}

const CATEGORY_TABS = ['gypsum', 'pintura', 'impermeabilizacion', 'empaste'] as const;
const CATEGORY_LABELS: Record<string, string> = {
  gypsum: 'Gypsum',
  pintura: 'Pintura',
  impermeabilizacion: 'Impermeab.',
  empaste: 'Empaste',
};

// ─── Residente: simple manual table ───────────────────────────────────────────

function ResidentePedidoView({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const createPedido = useCreatePedido(projectId);

  const [items, setItems] = useState<SimpleItem[]>([{ descripcion: '', cantidad: 1 }]);
  const [notas, setNotas] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const addRow = () => {
    setItems((prev) => [...prev, { descripcion: '', cantidad: 1 }]);
    // Focus the new description input on next render
    setTimeout(() => {
      const inputs = document.querySelectorAll<HTMLInputElement>('[data-simple-desc]');
      inputs[inputs.length - 1]?.focus();
    }, 30);
  };

  const updateItem = (idx: number, field: keyof SimpleItem, value: string | number) => {
    setItems((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const removeItem = (idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const validItems = items.filter((it) => it.descripcion.trim());
  const canSend = validItems.length > 0;

  const handleSubmit = async () => {
    if (!canSend) return;
    try {
      await createPedido.mutateAsync({
        tipo: 'materiales',
        notas: notas.trim() || null,
        items: validItems.map((it) => ({
          nombreCustom: it.descripcion.trim(),
          cantidad: Math.ceil(it.cantidad),
          esConsumibleAdicional: false,
        })),
      });
      toast('success', 'Pedido enviado a Compras');
      navigate(`/proyectos/${projectId}/pedidos`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      toast('error', msg ?? 'Error al crear pedido');
    }
    setShowConfirm(false);
  };

  return (
    <div className="p-4 max-w-2xl mx-auto pb-28">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="p-1 hover:bg-white/50 rounded">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-semibold">Pedido de Materiales</h1>
      </div>

      {/* Simple table */}
      <Card className="p-0 mb-4 overflow-hidden">
        {/* Table header */}
        <div className="grid grid-cols-[1fr_36px] gap-0 bg-gray-50 border-b border-gray-200 px-3 py-2">
          <span className="text-xs font-medium text-gray-500">Descripción</span>
          <span />
        </div>

        {/* Rows */}
        <div className="divide-y divide-gray-100">
          {items.map((item, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_36px] gap-0 items-center px-3 py-2">
              <input
                data-simple-desc
                type="text"
                value={item.descripcion}
                onChange={(e) => updateItem(idx, 'descripcion', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (idx === items.length - 1) addRow();
                  }
                }}
                placeholder="Nombre del material"
                className="w-full text-sm bg-transparent border-0 outline-none focus:ring-0 py-1 pr-2"
              />
              <button
                onClick={() => items.length > 1 ? removeItem(idx) : undefined}
                disabled={items.length === 1}
                className="flex items-center justify-center p-1 text-gray-300 hover:text-red-400 disabled:pointer-events-none transition ml-1"
                tabIndex={-1}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {/* Add row button */}
        <div className="border-t border-dashed border-gray-200">
          <button
            onClick={addRow}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-[#3c98ff] hover:bg-blue-50 transition"
          >
            <Plus className="w-4 h-4" />
            Agregar fila
          </button>
        </div>
      </Card>

      {/* Notas */}
      <Card className="p-4 mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notas para Compras <span className="text-gray-400 font-normal">(opcional)</span>
        </label>
        <textarea
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
          placeholder="Instrucciones especiales, urgencia, ubicación de entrega..."
          maxLength={300}
          rows={2}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3c98ff]/30 resize-none"
        />
        <p className="text-xs text-gray-400 text-right">{notas.length}/300</p>
      </Card>

      {/* Sticky bottom */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/80 backdrop-blur-md border-t border-gray-200 z-10">
        <Button className="w-full" disabled={!canSend} onClick={() => setShowConfirm(true)}>
          <Send className="w-4 h-4 mr-2" />
          Enviar Pedido ({validItems.length} ítem{validItems.length !== 1 ? 's' : ''})
        </Button>
        {!canSend && (
          <p className="text-xs text-gray-400 text-center mt-1">
            Ingrese al menos un material con descripción y cantidad
          </p>
        )}
      </div>

      {/* Confirm modal */}
      <Modal isOpen={showConfirm} onClose={() => setShowConfirm(false)} title="Confirmar Envío" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Se enviará un pedido con <strong>{validItems.length}</strong> ítem{validItems.length !== 1 ? 's' : ''} al equipo de Compras.
          </p>
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => setShowConfirm(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} isLoading={createPedido.isPending}>
              <Send className="w-4 h-4 mr-1" />
              Confirmar Envío
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─── Compras / Admin: full OT-based flow ──────────────────────────────────────

function ComprasPedidoView({ projectId }: { projectId: string }) {
  const navigate = useNavigate();

  const { data: balance, isLoading, error } = useMaterialBalance(projectId);
  const { data: equipos, isLoading: equiposLoading } = useEquipos();
  const createPedido = useCreatePedido(projectId);

  const [tipo, setTipo] = useState<PedidoTipo>('materiales');
  const [activeCategory, setActiveCategory] = useState<string>('gypsum');
  const [quantities, setQuantities] = useState<Map<string, ItemQuantity>>(new Map());
  const [consumibles, setConsumibles] = useState<ConsumibleItem[]>([]);
  const [equipoQuantities, setEquipoQuantities] = useState<Map<string, number>>(new Map());
  const [notas, setNotas] = useState('');
  const [excessModal, setExcessModal] = useState<{ materialId: string; nombre: string; exceso: number } | null>(null);
  const [excessJustification, setExcessJustification] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const materialsByCategory = useMemo(() => {
    const map = new Map<string, typeof balance>();
    if (!balance) return map;
    for (const item of balance) {
      const cat = item.material_categoria;
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(item);
    }
    // Sort each category's items alphabetically by name
    for (const [cat, items] of map.entries()) {
      map.set(cat, items!.sort((a, b) => a.material_nombre.localeCompare(b.material_nombre, 'es')));
    }
    return map;
  }, [balance]);

  const availableEquipos = useMemo(() => {
    if (!equipos) return [];
    return equipos
      .filter((e) => e.is_active && e.stock_disponible > 0)
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }, [equipos]);

  const totalItems = useMemo(() => {
    let count = 0;
    for (const q of quantities.values()) {
      if (q.cantidad > 0) count++;
    }
    for (const qty of equipoQuantities.values()) {
      if (qty > 0) count++;
    }
    count += consumibles.filter((c) => c.cantidad > 0 && c.nombre.trim()).length;
    return count;
  }, [quantities, equipoQuantities, consumibles]);

  const handleQuantityChange = (materialId: string, value: number) => {
    const newQty = new Map(quantities);
    if (value <= 0) {
      newQty.delete(materialId);
    } else {
      const existing = newQty.get(materialId);
      newQty.set(materialId, { materialId, cantidad: value, justificacion: existing?.justificacion ?? '' });
    }
    setQuantities(newQty);
  };

  const handleQuantityBlur = (materialId: string, nombre: string, totalOt: number, totalPedido: number) => {
    const qty = quantities.get(materialId);
    if (!qty || qty.cantidad <= 0) return;
    if (totalPedido + qty.cantidad > totalOt) {
      setExcessModal({ materialId, nombre, exceso: totalPedido + qty.cantidad - totalOt });
    }
  };

  const handleExcessJustification = (justificacion: string) => {
    if (!excessModal) return;
    const newQty = new Map(quantities);
    const existing = newQty.get(excessModal.materialId);
    if (existing) {
      newQty.set(excessModal.materialId, { ...existing, justificacion });
    }
    setQuantities(newQty);
    setExcessModal(null);
  };

  const addConsumible = () => {
    setConsumibles([...consumibles, { nombre: '', unidad: '', cantidad: 0, justificacion: '' }]);
  };

  const updateConsumible = (idx: number, field: keyof ConsumibleItem, value: string | number) => {
    const updated = [...consumibles];
    updated[idx] = { ...updated[idx], [field]: value };
    setConsumibles(updated);
  };

  const removeConsumible = (idx: number) => {
    setConsumibles(consumibles.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    const items: Array<{
      materialId?: string | null;
      equipoId?: string | null;
      nombreCustom?: string | null;
      unidadCustom?: string | null;
      cantidad: number;
      esConsumibleAdicional?: boolean;
      justificacion?: string | null;
    }> = [];

    for (const q of quantities.values()) {
      if (q.cantidad > 0) {
        items.push({ materialId: q.materialId, cantidad: q.cantidad, justificacion: q.justificacion || null });
      }
    }
    for (const [equipoId, qty] of equipoQuantities.entries()) {
      if (qty > 0) items.push({ equipoId, cantidad: qty });
    }
    for (const c of consumibles) {
      if (c.cantidad > 0 && c.nombre.trim()) {
        items.push({
          nombreCustom: c.nombre,
          unidadCustom: c.unidad.trim() || null,
          cantidad: c.cantidad,
          esConsumibleAdicional: true,
          justificacion: c.justificacion || null,
        });
      }
    }

    if (items.length === 0) return;

    try {
      await createPedido.mutateAsync({ tipo, notas: notas.trim() || null, items });
      toast('success', 'Pedido enviado exitosamente');
      navigate(`/proyectos/${projectId}/pedidos`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      toast('error', msg ?? 'Error al crear pedido');
    }
    setShowConfirm(false);
  };

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-600 mb-2">Error al cargar balance de materiales</p>
        <Button variant="secondary" onClick={() => window.location.reload()}>Reintentar</Button>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-3xl mx-auto pb-24">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="p-1 hover:bg-white/50 rounded">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-semibold">Nuevo Pedido</h1>
      </div>

      {/* Type selector */}
      <Card className="p-4 mb-4">
        <p className="text-sm font-medium text-gray-700 mb-3">Tipo de pedido</p>
        <div className="flex gap-2">
          {(['materiales', 'equipos', 'ambos'] as PedidoTipo[]).map((t) => (
            <button
              key={t}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition ${
                tipo === t ? 'bg-[#3c98ff] text-white' : 'bg-white/60 text-gray-600 hover:bg-white/80'
              }`}
              onClick={() => {
                setTipo(t);
                // Equipo-only pedidos cannot have consumibles adicionales
                if (t === 'equipos') setConsumibles([]);
              }}
            >
              {t === 'materiales' ? 'Solo materiales' : t === 'equipos' ? 'Solo equipos' : 'Ambos'}
            </button>
          ))}
        </div>
      </Card>

      {/* Materials section */}
      {(tipo === 'materiales' || tipo === 'ambos') && (
        <>
          <div className="flex gap-1 mb-3 bg-white/40 rounded-lg p-1">
            {CATEGORY_TABS.map((cat) => {
              const catItems = materialsByCategory.get(cat);
              if (!catItems || catItems.length === 0) return null;
              return (
                <button
                  key={cat}
                  className={`flex-1 py-2 px-2 rounded-md text-sm font-medium transition ${
                    activeCategory === cat ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setActiveCategory(cat)}
                >
                  {CATEGORY_LABELS[cat] ?? cat}
                </button>
              );
            })}
          </div>

          <div className="space-y-2 mb-4">
            {(materialsByCategory.get(activeCategory) ?? []).map((m) => {
              const qty = quantities.get(m.material_id);
              const pendiente = m.pendiente;
              const exceeds = qty ? (m.total_pedido + qty.cantidad > m.total_ot) : false;

              return (
                <Card key={m.material_id} className="p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{m.material_nombre}</p>
                      {m.material_sku && <p className="text-xs text-gray-400">{m.material_sku}</p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs text-center mb-2">
                    <div>
                      <p className="text-gray-400">OT Total</p>
                      <p className="font-medium">{m.total_ot}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Ya pedido</p>
                      <p className="font-medium">{m.total_pedido}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Pendiente</p>
                      <p className={`font-medium ${pendiente > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                        {pendiente}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500 whitespace-nowrap">Pedir ahora:</label>
                    <button
                      className="px-2 py-1 text-[10px] font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
                      onClick={() => {
                        const value = Math.max(0, pendiente);
                        handleQuantityChange(m.material_id, value);
                        if (m.total_pedido + value > m.total_ot) {
                          setExcessModal({ materialId: m.material_id, nombre: m.material_nombre, exceso: m.total_pedido + value - m.total_ot });
                        }
                      }}
                    >
                      Total
                    </button>
                    <input
                      type="number"
                      min={0}
                      step="any"
                      value={qty?.cantidad ?? ''}
                      placeholder={String(Math.max(0, pendiente))}
                      onChange={(e) =>
                        handleQuantityChange(m.material_id, Number(e.target.value) || 0)
                      }
                      onBlur={() =>
                        handleQuantityBlur(m.material_id, m.material_nombre, m.total_ot, m.total_pedido)
                      }
                      className={`w-24 px-2 py-1 text-sm border rounded ${
                        exceeds ? 'border-orange-400 bg-orange-50' : 'border-gray-200'
                      }`}
                    />
                    <span className="text-xs text-gray-400">{m.material_unidad}</span>
                  </div>
                  {pendiente <= 0 && !qty && (
                    <p className="text-xs text-amber-600 mt-1">Ya se pidió la totalidad. Un nuevo pedido requiere justificación.</p>
                  )}
                  {exceeds && qty?.justificacion && (
                    <p className="text-xs text-orange-600 mt-1">Exceso justificado</p>
                  )}
                  {exceeds && !qty?.justificacion && qty && (
                    <p className="text-xs text-red-600 mt-1">Excede la OT — requiere justificación</p>
                  )}
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Equipos section */}
      {(tipo === 'equipos' || tipo === 'ambos') && (
        <Card className="p-4 mb-4">
          <p className="text-sm font-medium text-gray-700 mb-3">Equipos y Herramientas</p>
          {equiposLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : availableEquipos.length === 0 ? (
            <p className="text-xs text-gray-400">No hay equipos disponibles en inventario</p>
          ) : (
            <div className="space-y-2">
              {availableEquipos.map((eq) => {
                const qty = equipoQuantities.get(eq.id) ?? 0;
                return (
                  <div key={eq.id} className="flex items-center gap-3 border border-gray-200 rounded-lg p-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{eq.nombre}</p>
                      <p className="text-xs text-gray-400">
                        {eq.codigo && <span>{eq.codigo} · </span>}
                        Disponible: {eq.stock_disponible} / {eq.stock_total}
                      </p>
                    </div>
                    <input
                      type="number"
                      min={0}
                      max={eq.stock_disponible}
                      value={qty || ''}
                      placeholder="0"
                      onChange={(e) => {
                        const val = Math.min(Number(e.target.value) || 0, eq.stock_disponible);
                        const newMap = new Map(equipoQuantities);
                        if (val <= 0) newMap.delete(eq.id);
                        else newMap.set(eq.id, val);
                        setEquipoQuantities(newMap);
                      }}
                      className="w-20 px-2 py-1 text-sm border border-gray-200 rounded text-center"
                    />
                    <span className="text-xs text-gray-400 w-8">uds</span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* Consumibles adicionales — not applicable for equipo-only pedidos */}
      {tipo !== 'equipos' && (
        <Card className="p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-gray-700">Consumibles adicionales</p>
            <button onClick={addConsumible} className="flex items-center gap-1 text-sm text-[#3c98ff] hover:text-[#3c98ff]/80">
              <Plus className="w-4 h-4" /> Agregar
            </button>
          </div>
          {consumibles.length === 0 ? (
            <p className="text-xs text-gray-400">Items no contemplados en la OT</p>
          ) : (
            <div className="space-y-3">
              {consumibles.map((c, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 space-y-2">
                      <Input
                        label="Nombre"
                        value={c.nombre}
                        onChange={(e) => updateConsumible(idx, 'nombre', e.target.value)}
                        placeholder="Nombre del consumible"
                      />
                      <div className="flex gap-2">
                        <div className="w-24">
                          <Input
                            label="Cantidad"
                            type="number"
                            min={0}
                            value={c.cantidad || ''}
                            onChange={(e) => updateConsumible(idx, 'cantidad', Number(e.target.value) || 0)}
                          />
                        </div>
                        <div className="w-24">
                          <Input
                            label="Unidad"
                            value={c.unidad}
                            onChange={(e) => updateConsumible(idx, 'unidad', e.target.value)}
                            placeholder="ej: m2, kg"
                          />
                        </div>
                        <div className="flex-1">
                          <Input
                            label="Justificación"
                            value={c.justificacion}
                            onChange={(e) => updateConsumible(idx, 'justificacion', e.target.value)}
                            placeholder="Motivo (min 10 caracteres)"
                          />
                        </div>
                      </div>
                    </div>
                    <button onClick={() => removeConsumible(idx)} className="p-1 text-red-400 hover:text-red-600 mt-6">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Notas */}
      <Card className="p-4 mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Notas para Compras</label>
        <textarea
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
          placeholder="Instrucciones especiales para el despacho..."
          maxLength={300}
          rows={2}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3c98ff]/30 resize-none"
        />
        <p className="text-xs text-gray-400 text-right">{notas.length}/300</p>
      </Card>

      {/* Sticky bottom */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/80 backdrop-blur-md border-t border-gray-200 z-10">
        <Button className="w-full" disabled={totalItems === 0} onClick={() => setShowConfirm(true)}>
          <Send className="w-4 h-4 mr-2" />
          Enviar Pedido ({totalItems} ítem{totalItems !== 1 ? 's' : ''})
        </Button>
        {totalItems === 0 && (
          <p className="text-xs text-gray-400 text-center mt-1">
            Debe ingresar al menos una cantidad para enviar el pedido
          </p>
        )}
      </div>

      {/* Excess justification modal */}
      <Modal isOpen={!!excessModal} onClose={() => setExcessModal(null)} title="Alerta de Exceso" size="sm">
        {excessModal && (
          <div className="space-y-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-orange-800">{excessModal.nombre}</p>
                  <p className="text-orange-700 mt-1">
                    Excede la cantidad planificada en la OT por <strong>{excessModal.exceso}</strong> unidades
                  </p>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motivo del exceso <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={3}
                placeholder="Explique el motivo del exceso (mín. 10 caracteres)"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3c98ff]/30 resize-none"
                value={excessJustification}
                onChange={(e) => setExcessJustification(e.target.value)}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" onClick={() => { setExcessModal(null); setExcessJustification(''); }}>
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  if (excessJustification.length < 10) {
                    toast('error', 'La justificación debe tener al menos 10 caracteres');
                    return;
                  }
                  handleExcessJustification(excessJustification);
                  setExcessJustification('');
                }}
              >
                Enviar con justificación
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Confirm send modal */}
      <Modal isOpen={showConfirm} onClose={() => setShowConfirm(false)} title="Confirmar Envío" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Va a enviar un pedido con <strong>{totalItems}</strong> ítem{totalItems !== 1 ? 's' : ''}.
            El equipo de compras será notificado.
          </p>
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => setShowConfirm(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} isLoading={createPedido.isPending}>
              <Send className="w-4 h-4 mr-1" />
              Confirmar Envío
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─── Router: role-based dispatch ──────────────────────────────────────────────

export function NuevoPedidoPage() {
  const { id: projectId } = useParams<{ id: string }>();
  const { user } = useAuthStore();

  if (!projectId) return null;

  if (user?.role === 'residente') {
    return <ResidentePedidoView projectId={projectId} />;
  }

  return <ComprasPedidoView projectId={projectId} />;
}
